import { useState, useEffect } from 'react';
import { create } from 'zustand';
import {
  isApp,
  WalletInfo,
  WalletInfoByApp,
  WalletType,
  getWalletInfoByApp,
  switchWalletByApp,
  onWalletChangeByApp,
  getUserInfoByApp,
  openAppLogin,
  BridgeParams,
  signMessageByApp,
} from '@web3/base-utils';
import { useVisibility } from './useVisible';
import { userInfo } from 'os';

interface WalletStore {
  appWalletInfo: Partial<WalletInfoByApp>;
  appUserInfo: any;
  walletIsReady: boolean;
  userInfoIsReady: boolean;
  initialized: boolean;
  visibility: boolean;
  setWalletInfo: (info: Partial<WalletInfoByApp>) => void;
  setWalletIsReady: (isReady: boolean) => void;
  setUserInfo: (userInfo: any) => void;
  setUserInfoIsReady: (isReady: boolean) => void;
  setInitialized: () => void;
}

const useWalletStore = create<WalletStore>((set, get) => ({
  appWalletInfo: {},
  appUserInfo: {},
  walletIsReady: false,
  userInfoIsReady: false,
  initialized: false,
  visibility: false,
  setWalletInfo: (info) => set({ appWalletInfo: info }),
  setWalletIsReady: (isReady) => set({ walletIsReady: isReady }),
  setUserInfo: (userInfo) => set({ appUserInfo: userInfo }),
  setUserInfoIsReady: (isReady) => set({ userInfoIsReady: isReady }),
  setInitialized: () => set({ initialized: true }),
}));

export const useWalletByApp = () => {
  const {
    appWalletInfo,
    appUserInfo,
    setWalletInfo,
    setUserInfo,
    walletIsReady,
    userInfoIsReady,
    setWalletIsReady,
    setUserInfoIsReady,
    initialized,
    setInitialized,
  } = useWalletStore();
  const { isVisibility } = useVisibility();
  // const { toastWarn } = useToast();

  const initWalletInfo = async () => {
    const res = await getWalletInfoByApp();
    setWalletInfo(res.params.result || {});
    setWalletIsReady(true);
  };

  const initUserInfo = async () => {
    const res = await getUserInfoByApp();
    // toastWarn(res.params.vagueEmail);
    // console.log(res);
    setUserInfo(res?.params || {});
    setUserInfoIsReady(true);
  };

  useEffect(() => {
    if (!isApp() || initialized) return; // 只有在未初始化时执行

    initWalletInfo();
    initUserInfo();

    onWalletChangeByApp((res) => {
      setWalletInfo(res || {});
    });

    setInitialized(); // 设置为已初始化
  }, [initialized, setInitialized]);

  useEffect(() => {
    if (isVisibility && initialized) {
      initUserInfo();
    }
  }, [isVisibility]);

  const evmWalletInfoByApp = appWalletInfo?.supportChainList?.find(
    (item) => item.chainId === 1
  ) as WalletInfo;

  return {
    // evmWalletInfoByApp只有address和chainId
    evmWalletInfoByApp: {
      ...evmWalletInfoByApp,
      address: evmWalletInfoByApp?.address
        ? `0x${evmWalletInfoByApp.address}`
        : '',
    },
    userInfoByApp: appUserInfo,
    userInfoIsReadyByApp: userInfoIsReady,
    isLoginByApp: !!appWalletInfo.address || !!appUserInfo.uid,
    isSpotLoginByApp: true,
    hasWalletByApp: !!appWalletInfo.address,
    walletTypeByApp: WalletType[appWalletInfo.walletType],
    walletIdByApp: appWalletInfo.id,
    walletIsReadyByApp: walletIsReady,
    openLoginModalByApp: (params: BridgeParams = {}) => {
      switchWalletByApp(params);
    },
    openSpotLoginModalByApp: () => {
      openAppLogin((res) => {
        setUserInfo(res?.params || {});
      });
    },
    connectBybitWalletByApp: (params: BridgeParams = {}) => {
      switchWalletByApp(params);
    },
    createWalletByApp: (params: BridgeParams = {}) => {
      switchWalletByApp(params);
    },
    evmSignByApp: async (message: string) => {
      const { params } = await signMessageByApp({
        raw_data: message,
        sign_type: 'personalSign',
        chain_id: 1,
      });

      return params?.result;
    },
  };
};
